import { Input, Modal, Select, DatePicker, message, Pagination, Tabs, Upload, Button, notification, Checkbox } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { SavedSearches, orderColumns } from './constants';
import "./orders.css";
import { getUserDetails, makePostCall } from '../../utils/helper';
import { useAllowedModalities } from '../../hooks/useAllowedModalities';
import { mergeShortcuts, shortcutToFilterAndRange, SHORTCUTS_UPDATED_EVENT, getTechnicianFixedShortcuts } from '../../utils/shortcuts';
import dayjs from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import DeleteOrderModal from '../../components/DeleteOrderModal';
import AgGridTable from '../../components/AgGridTable';
import { debounce } from 'lodash';
import { FieldLabels, getPacsStatusLabel } from '../../constants/fieldLabels';
import TabPane from 'antd/es/tabs/TabPane';
import TextArea from 'antd/es/input/TextArea';
import ViewNotes from '../ManageNodes/ViewNotes';
import AddFileModal from '../../components/AddFileModal';
import OrderInfoPopup from '../OrderInfo';

const { RangePicker } = DatePicker;

const OrdersList = ({ StyledTabsComponent }) => {
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [saveFiltersModal, setSaveFiltersModal] = useState({ visible: false, data: {} });
  const [filterName, setFilterName] = useState(null);
  const [filters, setFilters] = useState({});
  const [modalityAllSelected, setModalityAllSelected] = useState(true);
  const [savedFilters, setSavedFilters] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const userDetails = getUserDetails();
  // Order deletion is gated behind a runtime env flag and limited to radiologists / HOD.
  const canDelete =
    !!(window as any).ENV_CONFIG?.ENABLE_ORDER_DELETE &&
    ['radiologist', 'hod'].includes(userDetails?.user_type);
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; record: any }>({ visible: false, record: null });
  const [deleting, setDeleting] = useState(false);
  // Technician-only: study list of technicians for the assignment dropdown.
  const isTechnician = userDetails?.user_type === 'technician';
  const [userList, setUserList] = useState([]);
  // Technician action modals (upload notes / view notes / patient & order info).
  const [addFileModal, setAddFileModal] = useState<{ visible: boolean; data?: any; modalType?: string; file?: any; fileType?: any; notes?: any; isNotes?: boolean }>({ visible: false, data: null, modalType: 'upload' });
  const [viewNotesModal, setViewNotesModal] = useState<{ visible: boolean; details: any } | null>({ visible: false, details: null });
  const [noteEditActive, setNoteEditActive] = useState(false);
  const [saveNoteHandler, setSaveNoteHandler] = useState(null);
  const [selectedNote, setSelectedNote] = useState({});
  const [orderInfoModal, setOrderInfoModal] = useState<{ visible: boolean; data: any }>({ visible: false, data: null });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  // Use dynamic configuration hooks
  const { modalityOptions } = useAllowedModalities();

  // Status filter options. The stored workflow value stays 'PENDING' (T005 —
  // relabel only, no DB/query changes), but the dropdown shows 'NEW' so the
  // filter applies 'PENDING' under the hood without confusing users.
  const statusOptions = [
    { label: 'NEW', value: 'PENDING' },
    { label: 'SCANNED', value: 'SCANNED' },
    { label: 'DRAFTED', value: 'DRAFTED' },
    { label: 'REVIEWED', value: 'REVIEWED' },
    { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
    { label: getPacsStatusLabel('NOT_FOR_REPORTING'), value: 'NOT_FOR_REPORTING' },
    { label: 'CANCELLED', value: 'CANCELLED' },
  ];
  const today = dayjs();
  const yesterday = dayjs().subtract(1, 'days');

  // Set the default value to [yesterday, today]
  const [dateRange, setDateRange] = useState([yesterday, today]);

  useEffect(() => {
    getSavedFilters();
    getQuickFilters();
    if (isTechnician) {
      getUsersList();
    }
  }, []);

  // Refresh chips when the user edits shortcuts from the profile dialog.
  useEffect(() => {
    const handler = () => getQuickFilters();
    window.addEventListener(SHORTCUTS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SHORTCUTS_UPDATED_EVENT, handler);
  }, []);

  useEffect(() => {
    console.log("Filters changed", filters);
    // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
    }
    debouncedFilter(filters);
  }, [filters]);

  useEffect(() => {
    console.log("date changed", filters);
    // if (Object.keys(filters).length) {
    debouncedFilter(filters);
    // }
  }, [dateRange]);

  // const debouncedFilter = debounce(() => { filterResults() }, 300);

  const debouncedFilter = useCallback(
    debounce((tempFilters) => {
      console.log('Debounced value:');
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const getSavedFilters = async () => {
    makePostCall('/get-saved-filters', { user_id: getUserDetails()?.username })
      .then(res => {
        // console.log("resp", res);
        setSavedFilters(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setSavedFilters([]);
      });
  }

  const getQuickFilters = async () => {
    // Technicians get fixed (non-editable) quick-access chips instead of the
    // configurable Shortcuts system (T006).
    if (isTechnician) {
      setQuickFilters(getTechnicianFixedShortcuts());
      return;
    }
    makePostCall('/get-my-shortcuts', { user_id: getUserDetails()?.username })
      .then(res => {
        setQuickFilters(mergeShortcuts(res.data?.data));
      })
      .catch(e => {
        console.log(e);
        setQuickFilters([]);
      });
  }

  const handleQuickFilters = (filter) => {
    // Compound shortcut: apply the whole filter set + date range at once.
    if (filter.filterRaw) {
      const { filters: f, dateRange: dr } = shortcutToFilterAndRange(filter.filterRaw);
      setFilters(f);
      if (dr) setDateRange(dr);
      return;
    }
    // LEGACY (pre-compound shortcuts) — kept for context. Quick filters used to
    // be single-key rows that merged one field=value onto the current filters.
    // Migration 20260618000003 converted all rows to compound qf_filter_json, so
    // every shortcut now takes the `filterRaw` path above and this is unused.
    //
    // setFilters(prevFilters => ({
    //   ...prevFilters,
    //   [filter.qf_key]: filter.qf_value
    // }));
  }

  const submitSaveFilters = () => {
    const payload = {
      filters: JSON.stringify(filters),
      uf_filter_name: filterName,
      user_id: getUserDetails()?.username
    };
    // console.log("Filters", payload);
    makePostCall('/save-my-filters', payload)
      .then(res => {
        // console.log("resp", res);
        getSavedFilters();
      })
      .catch(e => {
        console.log(e);
      });
  }

  // Open the delete confirmation modal for a soft-delete
  const handleDeleteClick = (record) => {
    setDeleteModal({ visible: true, record });
  };

  // Soft-delete (cancel) the order; reason (optional) comes from the modal
  const submitDelete = (reason) => {
    const record = deleteModal.record;
    if (!record) return;
    setDeleting(true);
    makePostCall('/delete-order', {
      order_id: record.pacs_ord_id,
      user_id: getUserDetails()?.username,
      reason,
    })
      .then((res) => {
        message.success('Order deleted');
        if (res.data?.dicom_error) {
          message.warning('Order hidden, but the study could not be deactivated in the DICOM server.');
        }
        setDeleteModal({ visible: false, record: null });
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch((e) => {
        message.error(e?.response?.data?.message || e.message || 'Failed to delete order');
      })
      .finally(() => setDeleting(false));
  };

  const clearFilters = () => {
    setFilters({});
    setDateRange(null);
    setModalityAllSelected(true);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults({}, 1, pagination.pageSize);
  }

  const filterResults = (tempFilters, page = 1, pageSize = 20) => {
    console.log("filterResults", tempFilters);

    setOrders({ ...orders, loading: true, data: [] });
    const payload = {
      ...tempFilters,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize
    };

    if (dateRange) {
      payload['from_date'] = dayjs(dateRange[0]).format('YYYYMMDD');
      payload['to_date'] = dayjs(dateRange[1]).format('YYYYMMDD');
      payload['po_study_date'] = dateRange;
    }

    console.log("payload", payload);

    makePostCall('/orders', payload)
      .then(res => {
        // console.log("resp", res);
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0
        })
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false, total: 0 })
      });
  }

  const handleFilterChange = (key, value) => {
    // console.log("handleFilterChange", key, value);
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    setFilters(temp_filters);
  }

  const handleFilterSelection = (selectedSavedFilter) => {
    // console.log("selectedSavedFilter", selectedSavedFilter);
    const filterString = selectedSavedFilter.uf_filter_json;
    const filterJson = JSON.parse(filterString);
    // console.log("filter json", filterJson);

    setFilters(filterJson);
  }

  // ---- Technician actions (mirrors the PACS list) ----------------------------
  // The Orders record is "flat" (patient / order_workflow / note_id live on the
  // record itself). The shared ViewNotes / OrderInfo / upload helpers expect the
  // PACS shape, so we wrap the record as { pacs_order: rec } when handing it off.

  const getUsersList = () => {
    makePostCall('/user-list', {})
      .then(res => {
        setUserList(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setUserList([]);
      });
  };

  // Assign an order to a technician (current user pinned to the top of the list).
  const assignTechnician = (rec, username, idx) => {
    if (!username || !rec?.pacs_ord_id) return;
    makePostCall('/assign-to-tech', { user_id: username, order_id: rec?.pacs_ord_id })
      .then(() => {
        message.success('Technician assigned successfully');
        // Update by ID so it survives pagination / re-ordering.
        setOrders(prev => {
          const data = [...(prev?.data || [])];
          const i = data.findIndex((o: any) => o.pacs_ord_id === rec?.pacs_ord_id);
          if (i !== -1) {
            data[i] = {
              ...data[i],
              order_workflow: { ...data[i].order_workflow, ow_assigned_technician: username },
            };
          }
          return { ...prev, data };
        });
      })
      .catch(e => {
        console.log(e);
        message.error(e?.response?.data?.message || e.message || 'Failed to assign technician');
      });
  };

  const addFile = (rec) => {
    setAddFileModal({ visible: true, data: { pacs_order: rec }, modalType: 'upload' });
  };

  const viewNotes = (rec) => {
    setSelectedNote(rec?.note_id || {});
    setViewNotesModal({ visible: true, details: { pacs_order: rec } });
    setNoteEditActive(false);
  };

  const handleNoteSelection = useCallback((note, _record) => {
    setSelectedNote(prevSelectedNote => {
      const previousNoteId = (prevSelectedNote as any)?.rn_id || null;
      const nextNoteId = note?.rn_id || null;
      const previousNoteText = (prevSelectedNote as any)?.rn_notes || '';
      const nextNoteText = note?.rn_notes || '';

      if (previousNoteId === nextNoteId && previousNoteText === nextNoteText) {
        return prevSelectedNote;
      }

      return note || {};
    });
  }, []);

  const showOrdDetails = (rec) => {
    setOrderInfoModal({ visible: true, data: { pacs_order: rec } });
  };

  const closeOrdInfo = () => {
    setOrderInfoModal({ visible: false, data: null });
  };

  return (
    <div>
      <div style={{ padding: '0.75rem 1.25rem 0' }}>
        <div
          style={{
            background: '#FFFFFF',
            borderRadius: '0.75rem',
            border: '1px solid #E2E8F1',
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
          }}
        >
          {/* Quick filters + action buttons share one row to save vertical space */}
          <div className="filter-toolbar">
            <div className="filter-toolbar-main">
              <SavedSearches
                savedFilters={savedFilters || []}
                handleFilterSelection={handleFilterSelection}
                quickFilters={quickFilters || []}
                handleQuickFilters={handleQuickFilters}
                currentFilters={filters}
              />
            </div>
            <div className="filter-toolbar-actions">
              <button
                className="btn-secondary btn-compact"
                onClick={() => {
                  clearFilters();
                }}
              >
                Clear Search
              </button>
              <button
                className="btn-primary btn-compact"
                onClick={() => {
                  setSaveFiltersModal({ visible: true });
                }}
              >
                Save Filters
              </button>
              <button
                className="btn-primary btn-compact"
                onClick={() => filterResults(filters)}
              >
                Search
              </button>
            </div>
          </div>
          <div className="filter-layout">
            <div className="ord-filters-row filter-layout-inputs">
              <div className="ord-field ord-field--name">
                <FyzksInput
                  size="small"
                  placeholder={FieldLabels.PATIENT_NAME}
                  value={filters['pat_name'] || ''}
                  onChange={e => handleFilterChange('pat_name', e.target.value)}
                />
              </div>

              <div className="ord-field ord-field--id">
                <FyzksInput
                  size="small"
                  placeholder={FieldLabels.PATIENT_ID}
                  value={filters['pat_pin'] || ''}
                  onChange={e => handleFilterChange('pat_pin', e.target.value)}
                />
              </div>

              <div className="ord-field ord-field--acc">
                <FyzksInput
                  size="small"
                  placeholder="Acc. No"
                  value={filters['po_acc_no'] || ''}
                  onChange={e => handleFilterChange('po_acc_no', e.target.value)}
                />
              </div>

              <div className="ord-field ord-field--body">
                <FyzksInput
                  size="small"
                  placeholder="Body Part / Study Desc"
                  value={filters['po_body_part'] || ''}
                  onChange={e => handleFilterChange('po_body_part', e.target.value)}
                />
              </div>

              <div className="ord-field ord-field--ordno">
                <FyzksInput
                  size="small"
                  placeholder="Order No"
                  value={filters['his_ord_no'] || ''}
                  onChange={e => handleFilterChange('his_ord_no', e.target.value)}
                />
              </div>

              <div className="ord-field ord-field--status">
                <Select
                  size="small"
                  mode="multiple"
                  allowClear
                  placeholder="Status"
                  value={filters['pacs_status']}
                  style={{ width: '100%' }}
                  options={statusOptions}
                  onChange={val => handleFilterChange('pacs_status', val)}
                />
              </div>

              <div className="ord-field ord-field--date">
                <RangePicker
                  size="small"
                  style={{ width: '100%' }}
                  placeholder={['Request Date', '']}
                  value={dateRange}
                  onChange={val => {
                    if (val) {
                      setDateRange([val[0], val[1]]);
                    } else {
                      setDateRange(null);
                    }
                  }}
                />
              </div>
            </div>

            <div className="filter-layout-modalities">
              <div className="filter-modalities-header">
                <span className="filter-modalities-label">Modality</span>
                <Checkbox
                  className="filter-modalities-selectall"
                  checked={modalityAllSelected}
                  indeterminate={!modalityAllSelected && (filters['modality']?.length > 0)}
                  onChange={e => {
                    setModalityAllSelected(e.target.checked);
                    handleFilterChange('modality', []);
                  }}
                >
                  Select all
                </Checkbox>
              </div>
              <Checkbox.Group
                value={modalityAllSelected ? modalityOptions.map(o => o.value) : (filters['modality'] || [])}
                onChange={vals => {
                  const allChecked = vals.length === modalityOptions.length;
                  setModalityAllSelected(allChecked);
                  handleFilterChange('modality', allChecked ? [] : vals);
                }}
              >
                {modalityOptions.map(opt => (
                  <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
                ))}
              </Checkbox.Group>
            </div>
          </div>
        </div>
        <div className="orders-list">
          <AgGridTable
            height="calc(100vh - 18rem)"
            loading={orders.loading}
            rowData={orders.data || []}
            rowKey={(rec) => rec?.pacs_ord_id ?? rec?.po_acc_no}
            getRowClass={(rec) => {
              const priority = (rec?.po_priority ?? rec?.pacs_order?.po_priority)?.toUpperCase();
              if (priority === 'EMERGENCY') return 'pacs-emergency-row';
              if (priority === 'ASAP') return 'pacs-asap-row';
              return '';
            }}
            columns={orderColumns({
              canDelete,
              onDelete: handleDeleteClick,
              isTechnician,
              addFile,
              viewNotes,
              showOrdDetails,
              assignTechnician,
              userList,
              currentUser: userDetails,
            })}
          />
          {/* External pagination for server-side paging */}
          <div className="pacs-ag-grid-pagination">
            <Pagination
              size="small"
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={orders?.total || 0}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={(page, pageSize) => {
                setPagination({ current: page, pageSize });
                filterResults(filters, page, pageSize);
              }}
              showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
            />
          </div>

          {saveFiltersModal.visible && (
            <Modal
              style={{
                height: 'auto',
              }}
              styles={{
                wrapper: {
                  transformOrigin: 'top center',
                },
              }}
              className="save-filter-modal"
              onCancel={() => {
                setSaveFiltersModal({ visible: false });
              }}
              okButtonProps={{ disabled: !filterName }}
              onOk={submitSaveFilters}
              open={saveFiltersModal.visible}
            >
              <div className="pt-4">
                <label>Enter a name for this filter set:</label>
                <Input
                  placeholder="Type the name for the filters"
                  width={300}
                  onChange={e => setFilterName(e.target.value)}
                />
              </div>
            </Modal>
          )}

          {deleteModal.visible && (
            <DeleteOrderModal
              open={deleteModal.visible}
              patientName={deleteModal.record?.patient?.pat_name}
              accessionNumber={deleteModal.record?.po_acc_no}
              loading={deleting}
              onConfirm={submitDelete}
              onCancel={() => setDeleteModal({ visible: false, record: null })}
            />
          )}

          {/* Documentation Uploads (technician action) */}
          {addFileModal && addFileModal?.visible && (
            <AddFileModal
              open={addFileModal.visible}
              data={addFileModal.data}
              title="Documentation Uploads"
              initialType={addFileModal.modalType || 'upload'}
              onClose={() => setAddFileModal({ visible: false })}
              onUploaded={() => filterResults(filters, pagination.current, pagination.pageSize)}
            />
          )}

          {/* View RIS Notes (technician action) */}
          {viewNotesModal && viewNotesModal.visible && (
            <Modal
              open={viewNotesModal.visible}
              onCancel={() => {
                if (noteEditActive) {
                  setNoteEditActive(false);
                  return;
                }
                setViewNotesModal(null);
                setNoteEditActive(false);
              }}
              onOk={async () => {
                if (saveNoteHandler) {
                  const saved = await saveNoteHandler();
                  if (!saved) {
                    return;
                  }
                }
              }}
              style={{ width: '100%', height: '100%', top: '5%' }}
              width={'90%'}
              okButtonProps={{
                style: { marginTop: '20px' },
                disabled: !noteEditActive,
              }}
              okText="Save"
              cancelText={noteEditActive ? 'Cancel' : 'Close'}
            >
              <ViewNotes
                handleNoteSelection={handleNoteSelection}
                selectedNote={selectedNote}
                viewNotesModal={viewNotesModal}
                isEditMode={noteEditActive}
                onEditNoteStateChange={setNoteEditActive}
                onSaveNoteRegister={setSaveNoteHandler}
              />
            </Modal>
          )}

          {/* Patient & Order Info (technician action) */}
          {orderInfoModal && orderInfoModal.visible && (
            <Modal
              open={orderInfoModal.visible}
              onCancel={closeOrdInfo}
              footer={[
                <Button key="cancel" onClick={closeOrdInfo}>
                  Cancel
                </Button>,
              ]}
              width={'43.75rem'}
            >
              <OrderInfoPopup
                data={orderInfoModal.data}
                closeModal={closeOrdInfo}
                refreshTable={() =>
                  filterResults(filters, pagination.current, pagination.pageSize)
                }
              />
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrdersList;
